'use client';

import {
  ChatActiveIcon,
  ChatIcon,
  FavoritesActiveIcon,
  FavoritesIcon,
  HomeActiveIcon,
  HomeIcon,
  ProfileIcon,
  SellIcon,
} from '@c/icons/bottomNavbar';
import Avatar from '@ui/Avatar';
import { useAuth } from 'context/AuthContext';
import { useNotification } from 'context/NotificationContext';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ComponentType, useEffect, useState } from 'react';

const pathsWithoutBottomBar = [
  '/become-a-seller',
  '/create-listing',
  '/edit-listing',
  '/login',
  '/signup',
  '/signout',
  '/verify-email',
  '/verify-id',
  '/verify-phone',
  '/forgot-password',
  '/checkout',
  '/dashboard',
  '/welcome',
];

type BottomNavTab = {
  name: string;
  url: string;
  icon: ComponentType<{ className?: string }>;
  activeIcon?: ComponentType<{ className?: string }>;
  hasNotification: boolean;
};

export const BottomNavbar = () => {
  const pathname = usePathname();
  const { userDoc } = useAuth();
  const { unreadNotificationsCount } = useNotification();
  const [showBar, setShowBar] = useState(true);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY <= 0) {
        setShowBar(true);
      } else if (currentScrollY > lastScrollY) {
        setShowBar(false);
      } else {
        setShowBar(true);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const Tabs: BottomNavTab[] = [
    {
      name: 'Home',
      url: '/',
      icon: HomeIcon,
      activeIcon: HomeActiveIcon,
      hasNotification: false,
    },
    {
      name: 'Favorites',
      url: '/dashboard/favorites',
      icon: FavoritesIcon,
      activeIcon: FavoritesActiveIcon,
      hasNotification: false,
    },
    {
      name: 'sell',
      url: '/dashboard/listings',
      icon: SellIcon,
      hasNotification: false,
    },
    {
      name: 'chat',
      icon: ChatIcon,
      url: '/dashboard/messages',
      activeIcon: ChatActiveIcon,
      hasNotification: unreadNotificationsCount > 0,
    },
  ];

  const isInProfilePage = pathname === '/dashboard/my-profile';
  // Logged-out users (no userDoc) are sent to login instead of the profile
  // page, returning to the profile once authenticated.
  const profileHref = userDoc
    ? '/dashboard/my-profile'
    : '/login?redirect=/dashboard/my-profile';

  if (
    pathname &&
    pathsWithoutBottomBar.some((route) => pathname.startsWith(route))
  )
    return null;

  return (
    <motion.div
      initial={{ y: 0, opacity: 1 }}
      animate={{
        y: showBar ? 0 : 100,
        opacity: showBar ? 1 : 0,
      }}
      transition={{
        duration: 0.2,
        ease: 'easeInOut',
      }}
      className={`fixed bottom-0 z-50 flex w-full bg-white lg:hidden ${
        showBar ? 'pointer-events-auto' : 'pointer-events-none'
      }`}
    >
      {Tabs.map((tab) => {
        const isActive = pathname === tab.url;
        const Icon = isActive ? tab.activeIcon ?? tab.icon : tab.icon;
        return (
          <Link
            href={tab.url}
            className={`relative flex flex-1 flex-col items-center justify-between gap-1 py-4 ${
              isActive ? 'text-brand-secondary' : 'text-text-muted'
            }`}
            key={tab.url}
          >
            {tab.hasNotification ? (
              <div className="absolute right-[26px] top-[10px] size-4 rounded-full bg-brand-secondary" />
            ) : null}
            <Icon className="size-14" />
            <span className="text-[1.25rem] font-medium capitalize">
              {tab.name}
            </span>
          </Link>
        );
      })}
      <Link
        href={profileHref}
        className="flex flex-1 flex-col items-center justify-between gap-1 py-4"
      >
        {userDoc ? (
          <div
            className={`rounded-full p-1 ${
              isInProfilePage ? 'border border-brand-secondary' : undefined
            }`}
          >
            <Avatar user={userDoc} size="bottomBar" circle />
          </div>
        ) : (
          <ProfileIcon className="size-12" />
        )}
        <span
          className={`text-[1.25rem] font-medium ${
            isInProfilePage ? 'text-brand-secondary' : 'text-text-muted'
          } `}
        >
          Profile
        </span>
      </Link>
    </motion.div>
  );
};
